import { APP_URL } from "../constants";
import {
  clearPortalTokens,
  getPortalTokens,
  setPortalTokens,
} from "../utils/merchant-portal-storage";

const PORTAL_BASE = `${APP_URL}/v1/portal`;

let refreshInFlight = null;

async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) {
    return { status_code: response.status, message: response.statusText };
  }
  try {
    return JSON.parse(text);
  } catch {
    return { status_code: response.status, message: text };
  }
}

async function refreshPortalTokens() {
  const { refresh_token } = getPortalTokens();
  if (!refresh_token) {
    return null;
  }
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = fetch(`${PORTAL_BASE}/auth/refresh`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refresh_token }),
  })
    .then(parseJsonResponse)
    .then((json) => {
      if (json.status_code === 200 && json.data) {
        setPortalTokens(json.data);
        return json.data.access_token;
      }
      clearPortalTokens();
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export async function portalRequest(path, options = {}) {
  const {
    method = "GET",
    body,
    auth = true,
    retryOnUnauthorized = true,
  } = options;

  const headers = {
    Accept: "application/json",
    ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
  };

  if (auth) {
    let { access_token } = getPortalTokens();
    if (!access_token) {
      access_token = await refreshPortalTokens();
    }
    if (access_token) {
      headers.Authorization = `Bearer ${access_token}`;
    }
  }

  const response = await fetch(`${PORTAL_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (
    auth &&
    response.status === 401 &&
    retryOnUnauthorized &&
    getPortalTokens().refresh_token
  ) {
    const newToken = await refreshPortalTokens();
    if (newToken) {
      return portalRequest(path, { ...options, retryOnUnauthorized: false });
    }
  }

  return parseJsonResponse(response);
}

export function portalLogin(identifier, pin) {
  return portalRequest("/auth/login", {
    method: "POST",
    body: { identifier, pin },
    auth: false,
  });
}

export function portalVerifyOtp(challenge_id, otp) {
  return portalRequest("/auth/verify-otp", {
    method: "POST",
    body: { challenge_id, otp },
    auth: false,
  });
}

export function portalResendOtp(challenge_id) {
  return portalRequest("/auth/resend-otp", {
    method: "POST",
    body: { challenge_id },
    auth: false,
  });
}

export async function portalLogout() {
  try {
    await portalRequest("/auth/logout", { method: "POST" });
  } finally {
    clearPortalTokens();
  }
}

export function portalLogoutAll() {
  return portalRequest("/auth/logout-all", { method: "POST" });
}

export function portalGetMe() {
  return portalRequest("/me");
}

export function portalUpdateMe(payload) {
  return portalRequest("/me", { method: "PATCH", body: payload });
}

export function portalChangePin(payload) {
  return portalRequest("/me/pin", { method: "PUT", body: payload });
}

export function portalUpdateIpWhitelist(ips) {
  return portalRequest("/me/ip-whitelist", {
    method: "PUT",
    body: { ips },
  });
}

export function portalListSessions() {
  return portalRequest("/me/sessions");
}

export function portalRevokeSession(sid) {
  return portalRequest(`/me/sessions/${encodeURIComponent(sid)}`, {
    method: "DELETE",
  });
}

export function portalLoginActivity(page = 1, size = 20) {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  return portalRequest(`/me/login-activity?${params}`);
}

export function portalGetBalances(currency) {
  const params = currency
    ? `?${new URLSearchParams({ currency })}`
    : "";
  return portalRequest(`/balances${params}`);
}

export function portalListTransactions(query = {}) {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && `${value}`.length > 0) {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  return portalRequest(`/transactions${qs ? `?${qs}` : ""}`);
}

export function portalGetTransaction(id) {
  return portalRequest(`/transactions/${encodeURIComponent(id)}`);
}

export function portalListSubAccounts(query = {}) {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && `${value}`.length > 0) {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  return portalRequest(`/sub-accounts${qs ? `?${qs}` : ""}`);
}

export function portalGetSubAccount(id) {
  return portalRequest(`/sub-accounts/${encodeURIComponent(id)}`);
}

export function portalCreateSubAccount(body) {
  return portalRequest("/sub-accounts", { method: "POST", body });
}

export function portalUpdateSubAccount(id, body) {
  return portalRequest(`/sub-accounts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body,
  });
}

export function portalDeleteSubAccount(id) {
  return portalRequest(`/sub-accounts/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function portalListSubAccountPayouts(query = {}) {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && `${value}`.length > 0) {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  return portalRequest(`/sub-accounts/payouts${qs ? `?${qs}` : ""}`);
}

export async function portalExportTransactionsCsv(query = {}) {
  const params = new URLSearchParams({ format: "csv" });
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && `${value}`.length > 0) {
      params.set(key, String(value));
    }
  });

  let { access_token } = getPortalTokens();
  if (!access_token) {
    access_token = await refreshPortalTokens();
  }
  if (!access_token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(
    `${PORTAL_BASE}/transactions/export?${params}`,
    {
      headers: { Authorization: `Bearer ${access_token}` },
    },
  );

  if (response.status === 401 && getPortalTokens().refresh_token) {
    const newToken = await refreshPortalTokens();
    if (newToken) {
      return portalExportTransactionsCsv(query);
    }
  }

  if (!response.ok) {
    const err = await parseJsonResponse(response);
    throw new Error(err.message || "Export failed");
  }

  return response.blob();
}

export { refreshPortalTokens, clearPortalTokens, getPortalTokens };
